import React, { Suspense } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Switch, Route, Redirect } from "react-router-dom";
import { ConnectedRouter } from "connected-react-router";
import LoginPage from "modules/LoginPage";
import NavigationLayout from "../layouts/NavigationLayout";
import routePaths from "./routePaths";
import { features } from "accessControl/features";
import { roles } from "accessControl/roles";
import { ProtectedRoute } from "shared/Auth";
import ScreenSize from "shared/ScreenSize";
import AccessDenied from "../modules/AccessDenied";
import PageLoader from "../shared/PageLoader";
import OrderDetails from "../modules/UserInfoPage/sections/OrderDetails";
const HomePage = React.lazy(() => import("modules/HomePage"));
const DocsPage = React.lazy(() => import("modules/DocsPage"));
const ErrorPage = React.lazy(() => import("modules/ErrorPage"));
const MobileBlockerPage = React.lazy(() => import("modules/MobileBlockerPage"));

/* Entities - Offline Payments */
const OfflinePaymentsPage = React.lazy(() =>
  import("modules/OfflinePaymentsPage")
);

const CreateSalesLink = React.lazy(() =>
  import("modules/OfflinePaymentsPage/sections/SalesLink/CreateSalesLink")
);
const ViewSalesLink = React.lazy(() =>
  import("modules/OfflinePaymentsPage/sections/SalesLink/ViewSalesLink")
);
const CreateSingaporeOfflineUpload = React.lazy(() =>
  import(
    "modules/OfflinePaymentsPage/sections/SingaporeOfflineUpload/CreateSingaporeOfflineUpload"
  )
);
const ViewSingaporeOfflineUpload = React.lazy(() =>
  import(
    "modules/OfflinePaymentsPage/sections/SingaporeOfflineUpload/ViewSingaporeOfflineUpload"
  )
);
const CreateGeneralOfflineUpload = React.lazy(() =>
  import(
    "modules/OfflinePaymentsPage/sections/GeneralOfflineUpload/CreateGeneralOfflineUpload"
  )
);
const ViewGeneralOfflineUpload = React.lazy(() =>
  import(
    "modules/OfflinePaymentsPage/sections/GeneralOfflineUpload/ViewGeneralOfflineUpload"
  )
);
const ViewGroupOrder = React.lazy(() =>
  import("modules/UserInfoPage/sections/GroupOrder/ViewGroupOrder")
);
const CreateGroupOrder = React.lazy(() =>
  import("modules/UserInfoPage/sections/GroupOrder/CreateGroupOrder")
);
/* Entities - Refunds */
const RefundsPage = React.lazy(() => import("modules/RefundsPage"));
/* Entities - Quotation */
const QuotationPage = React.lazy(() => import("modules/QuotationPage"));
/* Entities - Purchase Orders */
const PurchaseOrderPage = React.lazy(() => import("modules/PurchaseOrderPage"));
/* Entities - Transfers Page */
const TransfersPage = React.lazy(() => import("modules/TransfersPage"));
/* Entities - Deferrals Page */
const DeferralsPage = React.lazy(() => import("modules/DeferralsPage"));
/* Entities - User Info Page */
const UserInfoPage = React.lazy(() => import("modules/UserInfoPage"));
/* Entities - Coupons */
const CouponsPage = React.lazy(() => import("modules/CouponsPage"));
/* Entities - Applications */
const ApplicationsPage = React.lazy(() => import("modules/ApplicationsPage"));

/* Entities - Franchise Page */
const FranchisePage = React.lazy(() => import("modules/FranchisePage"));
/* Entities - Franchise Users Page */
const FranchiseUsersPage = React.lazy(() =>
  import("modules/FranchiseUsersPage")
);
// Entities - Document Verification Page
const DocumentsPage = React.lazy(() => import("modules/DocumentsPage"));
// Entities - Subscriptions Page
const SubscriptionsPage = React.lazy(() => import("modules/SubscriptionsPage"));
// Entities - Stores Page
const StoresPage = React.lazy(() => import("modules/StoresPage"));

const RouteWrapper = (props) => {
  const isNotAuthenticated = !props.userInfo.isLoggedIn;

  if (isNotAuthenticated) {
    return <Redirect to={routePaths.loginPage} />;
  }

  if (
    props.userInfo.roles.length == 1 &&
    props.userInfo.roles[0] == "ROLE_STUDENT"
  ) {
    return <Redirect to={routePaths.accessDenied} />;
  }

  if (props.renderInfoForSmallerScreen) {
    return <Route component={MobileBlockerPage} isMobile={props.isMobile} />;
  }

  if (props.protect) {
    return ProtectedRoute(Route, props.protect, props.role)(props);
  }

  return <Route {...props} />;
};

RouteWrapper.propTypes = {
  userInfo: PropTypes.object,
  routePaths: PropTypes.object,
  protect: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  renderInfoForSmallerScreen: PropTypes.bool,
  isMobile: PropTypes.bool,
  role: PropTypes.string,
};

class RouterComponent extends React.Component {
  render() {
    const routes = {
      dashboard: [
        {
          exact: true,
          renderInfoForSmallerScreen: false,
          path: routePaths.homePage,
          component: HomePage,
          userInfo: this.props.userInfo,
        },
      ],
      docsPage: [
        {
          exact: true,
          renderInfoForSmallerScreen: false,
          path: routePaths.docsPage,
          component: DocsPage,
          userInfo: this.props.userInfo,
        },
      ],
      /* New Routes */
      /* Offline Payments */
      offlinePaymentsPage: [
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.offlinePayments__overview,
          component: OfflinePaymentsPage,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.offlinePayments__recordAndView,
          component: OfflinePaymentsPage,
          userInfo: this.props.userInfo,
          protect: features.PAYMENT_SETTLEMENT,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.offlinePayments__paymentLink,
          component: OfflinePaymentsPage,
          userInfo: this.props.userInfo,
          // TODO: add roles
          // protect: features.PAYMENT_RECEIPT,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.offlinePayments__salesLink,
          component: OfflinePaymentsPage,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.offlinePayments__createSalesLink,
          component: CreateSalesLink,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.offlinePayments__viewSalesLink,
          component: ViewSalesLink,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.offlinePayments__singaporeOfflineUpload,
          component: OfflinePaymentsPage,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.offlinePayments__createSingaporeOfflineUpload,
          component: CreateSingaporeOfflineUpload,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.offlinePayments__viewSingaporeOfflineUpload,
          component: ViewSingaporeOfflineUpload,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.offlinePayments__generalOfflineUpload,
          component: OfflinePaymentsPage,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.offlinePayments__createGeneralOfflineUpload,
          component: CreateGeneralOfflineUpload,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.offlinePayments__viewGeneralOfflineUpload,
          component: ViewGeneralOfflineUpload,
          userInfo: this.props.userInfo,
        },
      ],
      /* Refunds  */
      RefundsPage: [
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.refunds__overview,
          component: RefundsPage,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.refunds__recordAndView,
          component: RefundsPage,
          userInfo: this.props.userInfo,
          protect: features.PAYMENT_SETTLEMENT,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.refunds__dashboard,
          component: RefundsPage,
          userInfo: this.props.userInfo,
          // TODO: add protect
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.refunds__create,
          component: RefundsPage,
          userInfo: this.props.userInfo,
          // TODO: add protect
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.refunds__edit,
          component: RefundsPage,
          userInfo: this.props.userInfo,
          // TODO: add protect
        },
      ],
      /* Quotation */
      QuotationPage: [
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.quotation__dashboard,
          component: QuotationPage,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.quotation__create,
          component: QuotationPage,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.quotation__edit,
          component: QuotationPage,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.quotation__view,
          component: QuotationPage,
          userInfo: this.props.userInfo,
        },
      ],
      /* Purchase Orders */
      PurchaseOrderPage: [
        routePaths.purchaseOrder__dashboard,
        routePaths.purchaseOrder__create,
        routePaths.purchaseOrder__edit,
        routePaths.purchaseOrder__view,
        routePaths.purchaseOrder__customerCreate,
        routePaths.purchaseOrder__customerEdit,
        routePaths.purchaseOrder__customerView,
        routePaths.purchaseOrder__orderCreate,
        routePaths.purchaseOrder__orderEdit,
        routePaths.purchaseOrder__orderView,
        routePaths.purchaseOrder__deliveryNote,
      ].map((path) => ({
        exact: true,
        renderInfoForSmallerScreen: this.props.isMobile,
        path,
        component: PurchaseOrderPage,
        userInfo: this.props.userInfo,
      })),
      /* Transfer Screens */
      transferPage: [
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.transfers__overview,
          component: TransfersPage,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.transfers__batchAndPayment,
          component: TransfersPage,
          userInfo: this.props.userInfo,
          protect: features.PAYMENT_SETTLEMENT,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.transfers__currencyAndProgram,
          component: TransfersPage,
          userInfo: this.props.userInfo,
          protect: features.PAYMENT_SETTLEMENT,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.transfers__paymentTransfer,
          component: TransfersPage,
          userInfo: this.props.userInfo,
          protect: features.PAYMENT_SETTLEMENT,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.transfers__bulkBatchTransfer,
          component: TransfersPage,
          userInfo: this.props.userInfo,
          protect: features.PAYMENT_SETTLEMENT,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.transfers__bulkBatchTransferBySlug,
          component: TransfersPage,
          userInfo: this.props.userInfo,
          protect: features.PAYMENT_SETTLEMENT,
        },
      ],
      /* Coupons  */
      CouponsPage: [
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.coupons_overview,
          component: CouponsPage,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.coupons_dashboard,
          component: CouponsPage,
          userInfo: this.props.userInfo,
          protect: features.PAYMENT_SETTLEMENT,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.coupons_applyCoupon,
          component: CouponsPage,
          userInfo: this.props.userInfo,
          protect: features.PAYMENT_SETTLEMENT,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.coupons_paymentMode,
          component: CouponsPage,
          userInfo: this.props.userInfo,
          protect: features.PAYMENT_SETTLEMENT,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.coupons_revokeCoupon,
          component: CouponsPage,
          userInfo: this.props.userInfo,
          protect: features.PAYMENT_SETTLEMENT,
        },
      ],
      deferralsPage: [
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.deferrals__overview,
          component: DeferralsPage,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.deferrals__raiseRequest,
          component: DeferralsPage,
          userInfo: this.props.userInfo,
          protect: features.PAYMENT_SETTLEMENT,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.deferrals__reviewRequest,
          component: DeferralsPage,
          userInfo: this.props.userInfo,
          protect: features.PAYMENT_SETTLEMENT,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.deferrals__bulkRecordAndView,
          component: DeferralsPage,
          userInfo: this.props.userInfo,
          protect: features.PAYMENT_SETTLEMENT,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.deferrals__bulkRecordAndViewBySlug,
          component: DeferralsPage,
          userInfo: this.props.userInfo,
          protect: features.PAYMENT_SETTLEMENT,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.deferrals__bulkFeeRecordAndView,
          component: DeferralsPage,
          userInfo: this.props.userInfo,
          protect: features.PAYMENT_SETTLEMENT,
        },
      ],
      userInfoPage: [
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.userInfo__overview,
          component: UserInfoPage,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.userInfo__transactions,
          component: UserInfoPage,
          userInfo: this.props.userInfo,
          role: roles.ROLE_STUDENT_BUDDY,
          protect: features.PAYMENT_SETTLEMENT,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.userInfo__orderDetails,
          component: OrderDetails,
          role: roles.ROLE_STUDENT_BUDDY,
          userInfo: this.props.userInfo,
          protect: features.PAYMENT_SETTLEMENT,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.userInfo__nsdcLoginList,
          component: UserInfoPage,
          userInfo: this.props.userInfo,
          protect: features.PAYMENT_SETTLEMENT,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.userInfo__learnersLoginList,
          component: UserInfoPage,
          userInfo: this.props.userInfo,
          protect: features.PAYMENT_SETTLEMENT,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          protect: features.PAYMENT_SETTLEMENT,
          path: routePaths.userInfo__userMigration,
          component: UserInfoPage,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          protect: features.PAYMENT_SETTLEMENT,
          path: routePaths.userInfo__gguUsersUpload,
          component: UserInfoPage,
          userInfo: this.props.userInfo,
        },
        {
          // IRA-71246: IIT Delhi learner upload (same flow as GGU).
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          protect: features.PAYMENT_SETTLEMENT,
          path: routePaths.userInfo__iitdUsersUpload,
          component: UserInfoPage,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          protect: features.PAYMENT_SETTLEMENT,
          path: routePaths.userInfo__groupOrder,
          component: UserInfoPage,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          protect: features.PAYMENT_SETTLEMENT,
          path: routePaths.userInfo__createGroupOrder,
          component: CreateGroupOrder,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.userInfo__viewGroupOrder,
          component: ViewGroupOrder,
          userInfo: this.props.userInfo,
          protect: features.PAYMENT_SETTLEMENT,
        },
      ],
      ApplicationsPage: [
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.applications__overview,
          component: ApplicationsPage,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.applications__offerLetter,
          component: ApplicationsPage,
          userInfo: this.props.userInfo,
          protect: features.SHORTLISTING_CONFIG,
        },
      ],
      franchisePage: [
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.franchise__overview,
          component: FranchisePage,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.franchise__franchiseView,
          component: FranchisePage,
          userInfo: this.props.userInfo,
        },
      ],
      franchiseUsersPage: [
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.franchise__users,
          component: FranchiseUsersPage,
          userInfo: this.props.userInfo,
        },
      ],
      documentPage: [
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.document__overview,
          component: DocumentsPage,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.document__documentList,
          component: DocumentsPage,
          userInfo: this.props.userInfo,
        },
      ],
      subscriptionsPage: [
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.subscriptions__overview,
          component: SubscriptionsPage,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.subscriptions__dashboard,
          component: SubscriptionsPage,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.subscriptions__create,
          component: SubscriptionsPage,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.subscriptions__edit,
          component: SubscriptionsPage,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.subscriptions__cancel,
          component: SubscriptionsPage,
          userInfo: this.props.userInfo,
        },
      ],
      storesPage: [
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.stores__overview,
          component: StoresPage,
          userInfo: this.props.userInfo,
        },
        {
          exact: true,
          renderInfoForSmallerScreen: this.props.isMobile,
          path: routePaths.stores__storeList,
          component: StoresPage,
          userInfo: this.props.userInfo,
        },
      ],
      /* General Redirects */
      generalRedirects: [
        {
          exact: true,
          from: routePaths.offlinePayments,
          to: routePaths.offlinePayments__overview,
        },
        {
          exact: true,
          from: routePaths.refunds,
          to: routePaths.refunds__overview,
        },
        {
          exact: true,
          from: routePaths.transfers,
          to: routePaths.transfers__overview,
        },
        {
          exact: true,
          from: routePaths.quotation,
          to: routePaths.quotation__dashboard,
        },
        {
          exact: true,
          from: routePaths.purchaseOrder,
          to: routePaths.purchaseOrder__dashboard,
        },
        {
          exact: true,
          from: routePaths.coupons,
          to: routePaths.coupons_overview,
        },
        {
          exact: true,
          from: routePaths.deferrals,
          to: routePaths.deferrals__overview,
        },
        {
          exact: true,
          from: routePaths.userInfo,
          to: routePaths.userInfo__overview,
        },
        {
          exact: true,
          from: routePaths.applications,
          to: routePaths.applications__overview,
        },
        {
          exact: true,
          from: routePaths.franchise,
          to: routePaths.franchise__overview,
        },
        {
          exact: true,
          from: routePaths.document,
          to: routePaths.document__overview,
        },
        {
          exact: true,
          from: routePaths.subscriptions,
          to: routePaths.subscriptions__overview,
        },
        {
          exact: true,
          from: routePaths.stores,
          to: routePaths.stores__overview,
        },
      ],
      errorPage: [
        {
          renderInfoForSmallerScreen: false,
          component: ErrorPage,
          userInfo: this.props.userInfo,
        },
      ],
    };
    const mapRoutes = (routes) => {
      let map = [];
      let lastEntries = [];
      if (routes["generalRedirects"]) {
        lastEntries = [
          ...routes["generalRedirects"].map((r) => (
            <Redirect key={`${r.from}->${r.to}`} {...r} />
          )),
        ];
      }
      if (routes["errorPage"]) {
        lastEntries = [
          ...lastEntries,
          ...routes["generalRedirects"].map((r, i) => (
            <RouteWrapper key={r.path || `errorPage-${i}`} {...r} />
          )),
        ];
      }
      Object.keys(routes).forEach((route) => {
        switch (route) {
          case "generalRedirects":
          case "errorPage":
            break;
          default:
            map = [
              ...map,
              ...routes[route].map((r) => <RouteWrapper key={r.path} {...r} />),
            ];
            break;
        }
      });
      return [...map, ...lastEntries];
    };
    return (
      <ConnectedRouter history={this.props.history}>
        <div>
          <Switch>
            <Route
              path={routePaths.loginPage}
              component={LoginPage}
              isMobile={this.props.isMobile}
            />
            <Route path={routePaths.accessDenied} component={AccessDenied} />
            <NavigationLayout
              history={this.props.history}
              isMobile={this.props.isMobile}
              isContentFullWidth={/^\/program-launcher\/[a-zA-Z0-9-/]+$/.test(
                this.props.history?.location?.pathname
              )}
            >
              <Suspense fallback={<PageLoader />}>
                <Switch>{mapRoutes(routes)}</Switch>
              </Suspense>
            </NavigationLayout>
          </Switch>
        </div>
      </ConnectedRouter>
    );
  }
}

RouterComponent.propTypes = {
  history: PropTypes.object,
  userInfo: PropTypes.object,
  isMobile: PropTypes.bool,
};

function mapStateToProps(state, props) {
  return {
    router: state.router,
    history: props.history,
    userInfo: state.userInfo,
  };
}

export default connect(mapStateToProps)(ScreenSize(RouterComponent));
